import type { MonthlyAvgDataPoint } from '@/types/otc'

function mulberry32(seed: number) {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

function toSortedValues(series?: MonthlyAvgDataPoint[]): number[] {
  if (!series || series.length === 0) return []

  return [...series]
    .filter((point) => typeof point?.value === 'number' && Number.isFinite(point.value))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((point) => point.value)
}

function resample(values: number[], targetLength: number): number[] {
  if (targetLength <= 0) return []
  if (values.length === 0) return []
  if (values.length === 1) return Array.from({ length: targetLength }, () => values[0])
  if (targetLength === 1) return [values[values.length - 1]]

  return Array.from({ length: targetLength }, (_, idx) => {
    const t = (idx * (values.length - 1)) / (targetLength - 1)
    const leftIndex = Math.floor(t)
    const rightIndex = Math.min(values.length - 1, Math.ceil(t))
    const mix = t - leftIndex

    return values[leftIndex] * (1 - mix) + values[rightIndex] * mix
  })
}

function normalizeToRange(values: number[], min: number, max: number): number[] {
  if (values.length === 0) return []

  const sourceMin = Math.min(...values)
  const sourceMax = Math.max(...values)
  const sourceRange = sourceMax - sourceMin

  if (sourceRange <= Number.EPSILON) {
    const middle = (min + max) / 2
    return values.map(() => Math.round(middle))
  }

  return values.map((value) => {
    const ratio = (value - sourceMin) / sourceRange
    return Math.round(min + ratio * (max - min))
  })
}

function fallbackSeries(length: number, seed: number, min: number, max: number): number[] {
  const rng = mulberry32(Math.max(1, Math.floor(seed)))
  const center = (min + max) / 2
  const amplitude = (max - min) * 0.35

  return Array.from({ length }, (_, idx) => {
    const wave = Math.sin(idx * 0.9 + rng() * Math.PI) * amplitude
    const jitter = (rng() - 0.5) * (max - min) * 0.18
    return Math.round(clamp(center + wave + jitter, min, max))
  })
}

interface BuildSeriesInput {
  source?: MonthlyAvgDataPoint[]
  count: number
  min: number
  max: number
  seed: number
}

export function buildChartSeries({ source, count, min, max, seed }: BuildSeriesInput): number[] {
  const sortedValues = toSortedValues(source)
  if (sortedValues.length === 0) {
    return fallbackSeries(count, seed, min, max)
  }

  const sampled = resample(sortedValues, count)
  return normalizeToRange(sampled, min, max)
}
